import React, { useState, useEffect } from 'react';
import { ModelGardenItem } from './types';
import ProfilePictureRounded from '../../ProfilePictureRounded';
import { ReactComponent as ErodeIcon } from '../../../assets/icons/erode_icon.svg';
import { imageCache } from '../../../utils/imageCache';
import { getBandColor } from '../../../utils/modelsList';
import { Skeleton, SkeletonText } from "../../Skeleton";

interface ModelCardProps {
  model: ModelGardenItem;
  onClick: (model: ModelGardenItem) => void;
}

const ModelCard: React.FC<ModelCardProps> = ({ model, onClick }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  useEffect(() => {
    if (model.thumbnail) {
      imageCache.getSignedUrl(model.thumbnail).then(setThumbnailUrl);
    }
  }, [model.thumbnail]);
  return (
    <div
      className="eai-card-interactive overflow-hidden cursor-pointer flex flex-col h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-eai-cta-primary focus-visible:ring-offset-2"
      onClick={() => onClick(model)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(model);
        }
      }}
    >
      {/* Thumbnail placeholder - full width, extends to top */}
      <div className={`w-full h-40 flex items-center justify-center relative flex-shrink-0 ${thumbnailUrl ? '' : getBandColor(model.id)}`}>
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={model.name} className="w-full h-full object-cover" />
        ) : (
          <div className="flex items-center justify-center w-full h-full">
            <div className="w-16 h-16" style={{ filter: 'brightness(0) invert(1)' }}>
              <ErodeIcon className="w-full h-full" />
            </div>
          </div>
        )}
      </div>

      {/* Content section with padding - flex grow to push creator to bottom */}
      <div className="p-4 flex flex-col flex-grow">
        
        {/* Main content area that grows */}
        <div className="flex-grow">
          {/* Badge - only show if Community */}
          {model.type === 'Community' && (
            <div className="mb-3">
              <span className="eai-eyebrow inline-flex rounded-full bg-eai-cta-primary px-3 py-1 text-white">
                {model.type}
              </span>
            </div>
          )}

          <h3 className="eai-card-title mb-1 line-clamp-2 break-words">
            {model.name}
          </h3>

          {/* Model ID */}
          <p className="font-inter text-xs text-slate-400 mb-2 truncate" title={model.id}>
            {model.id}
          </p>

          {/* Description */}
          <p className="eai-description-sm line-clamp-2">
            {model.description}
          </p>
        </div>

        {/* Bottom section - always aligned */}
        <div>
          {/* Category and Tags */}
          <div className="flex flex-wrap gap-2 mb-4 mt-4">
            {model.modelKind === 'HISTORICAL' && (
              <span className="eai-tag-pill-sm border-eai-teal-deep/40 text-eai-teal-deep" data-testid="historical-pill">
                HISTORICAL
              </span>
            )}
            {model.category && (
              <span className="eai-tag-pill-sm">
                {model.category}
              </span>
            )}
            {model.tags?.slice(0, 3).map((tag, index) => (
              <span 
                key={index}
                className="eai-tag-pill-sm"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Divider line */}
          <hr className="border-t border-zinc-100 mb-3" />

          {/* Creator - larger profile picture and text */}
          <div className="flex items-center">
            <ProfilePictureRounded 
              pictureURL={model.creator.profilePictureUrl}
              diameter={32}
              className="mr-3"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
              }}
            />
            <span className="font-inter text-sm text-slate-600 font-medium">{model.creator.name}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder matching ModelCard's layout — keep the two in sync when the card changes.
export const ModelCardSkeleton = () => (
  <div className="eai-card-interactive flex h-full flex-col overflow-hidden">
    <Skeleton className="h-40 w-full shrink-0" radius="none" />
    <div className="flex flex-grow flex-col p-4">
      <div className="flex-grow">
        <Skeleton className="mb-1 h-5 w-3/4" />
        <Skeleton className="mb-2 h-3 w-1/2" />
        <SkeletonText lines={2} lineClassName="h-3" />
      </div>
      <div>
        <div className="mb-4 mt-4 flex flex-wrap gap-2">
          <Skeleton className="h-5 w-16" radius="full" />
          <Skeleton className="h-5 w-12" radius="full" />
        </div>
        <hr className="mb-3 border-t border-zinc-100" />
        <div className="flex items-center">
          <Skeleton className="mr-3 h-8 w-8" radius="full" />
          <Skeleton className="h-3.5 w-28" />
        </div>
      </div>
    </div>
  </div>
);

export default ModelCard;